// Admin webhook events (/admin/webhooks) — ops viewer for the payment webhook
// trail. Surfaces stranded events (unmatched / amount_mismatch); order_id can
// be null (unmatched) and Pakasir event ids are synthetic (`order:status`).

import { useState } from "react";
import { Link } from "react-router-dom";
import { dataClass } from "@ahcut/ui/recipes";
import { Badge } from "@/ui";
import { cn } from "@/lib/cn";
import { PageHeader } from "@/components/PageHeader";
import { Loading, ErrorState, EmptyState } from "@/components/States";
import { Pagination } from "@/components/Pagination";
import { FilterSelect } from "@/components/FilterSelect";
import { useAsync } from "@/lib/useAsync";
import { listAdminWebhookEvents } from "@/lib/admin";
import {
  formatDateTime,
  webhookStatusLabel,
  webhookStatusOptions,
  webhookStatusTone,
} from "@/lib/format";
import type { AdminWebhookEvent, Paged } from "@/lib/types";
import { AdminPanel } from "@/components/admin/AdminPanel";
import {
  DataCell,
  DataRow,
  DataTable,
  FilterBar,
  NoValue,
  type DataColumn,
} from "@/components/admin/DataTable";
import { dataCellClass } from "@/components/admin/classes";
import { BreakableText } from "@/components/admin/BreakableText";

const PAGE = 25;

const STATUS_OPTIONS = webhookStatusOptions();

const COLUMNS: readonly DataColumn[] = [
  { label: "Diterima" },
  { label: "Event" },
  { label: "Pesanan" },
  { label: "Status" },
  { label: "Diproses" },
];

export function AdminWebhookEvents() {
  const [status, setStatus] = useState("");
  const [offset, setOffset] = useState(0);

  const { data, loading, error, reload } = useAsync<Paged<AdminWebhookEvent>>(
    (s) => listAdminWebhookEvents({ status, limit: PAGE, offset }, s),
    [status, offset],
  );

  if (loading && !data) return <Loading label="Memuat event webhook…" />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  return (
    <div className="flex min-w-0 flex-col gap-6">
      <PageHeader
        title="Webhook"
        lede="Jejak event pembayaran. Event bermasalah (tak cocok / jumlah beda) butuh tindak lanjut ops."
      />

      <FilterBar label="Filter event webhook">
        <FilterSelect
          label="Status"
          value={status}
          options={STATUS_OPTIONS}
          onChange={(v) => {
            setOffset(0);
            setStatus(v);
          }}
        />
      </FilterBar>

      {!data || data.items.length === 0 ? (
        <EmptyState title="Tidak ada event">Tidak ada event webhook untuk filter ini.</EmptyState>
      ) : (
        <div className="flex min-w-0 flex-col gap-4">
          <AdminPanel>
            <DataTable
              label="Daftar event webhook"
              columns={COLUMNS}
              stackBelow="58rem"
              busy={loading}
            >
              {data.items.map((e) => (
                <DataRow key={e.id}>
                  <DataCell label="Diterima" className={dataCellClass}>
                    {formatDateTime(e.received_at)}
                  </DataCell>
                  {/* The provider sits above its event id (the audit log's
                      type-over-id pattern). Ids ("<uuid>:completed", 46
                      characters) are 12px mono: one line when the table has
                      room, otherwise broken at a hyphen or colon, never
                      narrower than 24 characters (two lines). */}
                  <DataCell label="Event">
                    <span className="block text-fg-2">{e.provider}</span>
                    <span className={cn(dataClass, "mt-0.5 block min-w-[24ch] text-xs")}>
                      <BreakableText text={e.event_id} at="id" />
                    </span>
                  </DataCell>
                  <DataCell label="Pesanan">
                    {e.order_id ? (
                      <Link to={`/admin/orders/${e.order_id}`}>
                        Lihat<span className="sr-only"> pesanan {e.order_id}</span>
                      </Link>
                    ) : (
                      <NoValue label="Tanpa pesanan" />
                    )}
                  </DataCell>
                  <DataCell label="Status">
                    {e.status ? (
                      <Badge tone={webhookStatusTone(e.status)} dot>
                        {webhookStatusLabel(e.status)}
                      </Badge>
                    ) : (
                      <NoValue label="Tanpa status" />
                    )}
                  </DataCell>
                  <DataCell label="Diproses" className={dataCellClass}>
                    {e.processed_at ? (
                      formatDateTime(e.processed_at)
                    ) : (
                      <NoValue label="Belum diproses" />
                    )}
                  </DataCell>
                </DataRow>
              ))}
            </DataTable>
          </AdminPanel>
          <Pagination
            total={data.total}
            limit={data.limit}
            offset={data.offset}
            onPage={setOffset}
            disabled={loading}
          />
        </div>
      )}
    </div>
  );
}
